import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
	ArrowLeft,
	Building2,
	Calendar,
	CheckCircle2,
	Clock,
	Download,
	ExternalLink,
	FileText,
	Loader2,
	Mail,
	Phone,
	User,
} from "lucide-react";
import { toast } from "sonner";
import { VendorLayout } from "@/components/layouts/VendorLayout";
import { useBuyerContext, useVendorProfile } from "@/contexts/vendor-buyer-context";
import { useTRPC } from "@/integrations/trpc/react";
import { useAuth } from "@/lib/auth";
import { PdfFormFillerV2 } from "@/components/pdf-filler";
import type { FormFillerData } from "@/components/pdf-filler/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";

export const Route = createFileRoute("/vendor/$buyerSlug/contracts/$contractId")({
	component: VendorContractDetailPage,
});

function getContractStatusColor(status: string) {
	switch (status) {
		case "active":
			return "bg-green-100 text-green-700";
		case "completed":
			return "bg-gray-100 text-gray-700";
		case "pending":
			return "bg-yellow-100 text-yellow-700";
		default:
			return "bg-gray-100 text-gray-700";
	}
}

// Type for award form from API
interface AwardForm {
	id: string;
	form_template_id: string;
	title: string;
	description: string | null;
	is_required: boolean;
	sort_order: number;
	form_templates: {
		id: string;
		title: string;
		pdf_storage_path: string | null;
	} | null;
}

// Type for form submission from API
interface FormSubmission {
	id: string;
	bid_award_form_id: string;
	status: "draft" | "submitted" | "reviewed";
	field_responses: Record<string, unknown>;
}

function VendorContractDetailPage() {
	const buyer = useBuyerContext();
	const vendorProfile = useVendorProfile();
	const { buyerSlug, contractId } = Route.useParams();
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const { user } = useAuth();
	const vendorEmail = user?.email ?? "";
	const signerEmail = vendorProfile.email || vendorEmail;

	// State for form modal
	const [formToFill, setFormToFill] = useState<AwardForm | null>(null);

	// Query contract details from the API
	const { data: contract, isLoading, error } = useQuery(
		trpc.vendorPortal.contracts.get.queryOptions({
			contractId,
		}),
	);

	// Track optimistic form submission status
	const [optimisticSubmissions, setOptimisticSubmissions] = useState<
		Record<string, "submitted" | "draft">
	>({});

	// Form submission mutation
	const submitFormMutation = useMutation(
		trpc.awards.formSubmissions.upsert.mutationOptions({
			onSuccess: (_data, variables) => {
				queryClient.invalidateQueries({ queryKey: ["vendorPortal", "contracts"] });
				toast.success(variables.isDraft ? "Draft saved" : "Form submitted successfully");
			},
			onError: (error, variables) => {
				// Revert optimistic update on error
				setOptimisticSubmissions((prev) => {
					const next = { ...prev };
					delete next[variables.bidAwardFormId];
					return next;
				});
				toast.error(`Failed to save form: ${error.message}`);
			},
		}),
	);

	// Get forms and submissions from contract data
	const awardForms = (contract?.awardForms ?? []) as AwardForm[];
	const formSubmissions = (contract?.formSubmissions ?? []) as FormSubmission[];

	// Helper to get submission for a form
	const getSubmissionForForm = (formId: string): FormSubmission | undefined => {
		return formSubmissions.find((s) => s.bid_award_form_id === formId);
	};

	// Helper to get effective status (includes optimistic updates)
	const getEffectiveFormStatus = (formId: string): "submitted" | "draft" | null => {
		// Check optimistic state first
		if (optimisticSubmissions[formId]) {
			return optimisticSubmissions[formId];
		}
		// Fall back to server data
		const submission = getSubmissionForForm(formId);
		return (submission?.status as "submitted" | "draft") ?? null;
	};

	// Calculate form completion status (with optimistic updates)
	const requiredForms = awardForms.filter((f) => f.is_required);
	const completedRequiredForms = requiredForms.filter((f) => {
		return getEffectiveFormStatus(f.id) === "submitted";
	});
	const allRequiredFormsComplete = requiredForms.length === 0 || completedRequiredForms.length === requiredForms.length;

	// Handler for opening form filler
	const handleOpenFormFiller = (form: AwardForm) => {
		if (!form.form_templates?.id) {
			toast.error("Form template not found");
			return;
		}
		setFormToFill(form);
	};

	// Handler for form completion
	const handleFormComplete = async (data: FormFillerData) => {
		if (!formToFill) return;

		// Validate that we have a valid vendor email
		if (!vendorEmail || !vendorEmail.includes("@")) {
			toast.error("Unable to save form: vendor email is not available");
			return;
		}

		// Optimistic update - set status immediately
		const newStatus = data.isDraft ? "draft" : "submitted";
		setOptimisticSubmissions((prev) => ({
			...prev,
			[formToFill.id]: newStatus,
		}));

		// Close the form modal immediately for better UX
		setFormToFill(null);

		try {
			await submitFormMutation.mutateAsync({
				bidAwardFormId: formToFill.id,
				vendorEmail,
				fieldResponses: data.fieldResponses,
				isSigned: !!data.signatureData,
				signerName: data.signatureData?.signerName,
				signerEmail: data.signatureData?.signerEmail,
				signatureData: data.signatureData ?? null,
				isDraft: data.isDraft,
			});
		} catch {
			// Error handled by mutation's onError callback (which reverts optimistic update)
		}
	};

	if (isLoading) {
		return (
			<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
				<div className="flex items-center justify-center py-12">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</VendorLayout>
		);
	}

	if (error || !contract) {
		return (
			<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
				<div className="text-center py-12">
					<h2 className="text-2xl font-bold">Contract not found</h2>
					<p className="text-muted-foreground mt-2">
						The contract you're looking for doesn't exist.
					</p>
					<Button asChild className="mt-4">
						<Link to="/vendor/$buyerSlug/contracts" params={{ buyerSlug }}>
							Back to Contracts
						</Link>
					</Button>
				</div>
			</VendorLayout>
		);
	}

	// Compute contract status and timeline
	const now = new Date();
	const primaryDate = contract.contractDates.find((d) => d.period_number === 1) ?? contract.contractDates[0];

	// Get the award status from the database (pending = forms incomplete, active = forms complete)
	const awardStatus = (contract as { status?: string }).status ?? "active";

	let timelineStatus: "active" | "completed" | "pending" = "pending";
	let daysRemaining = 0;
	let percentComplete = 0;
	let startDate: Date | null = null;
	let endDate: Date | null = null;

	if (primaryDate) {
		startDate = new Date(primaryDate.start_date);
		endDate = new Date(primaryDate.end_date);

		if (now < startDate) {
			timelineStatus = "pending";
			daysRemaining = Math.ceil(
				(endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
			);
		} else if (now > endDate) {
			timelineStatus = "completed";
			percentComplete = 100;
		} else {
			timelineStatus = "active";
			const totalDays = Math.ceil(
				(endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
			);
			const elapsedDays = Math.ceil(
				(now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
			);
			daysRemaining = Math.max(0, totalDays - elapsedDays);
			percentComplete = Math.round((elapsedDays / totalDays) * 100);
		}
	}

	// Display status: use award status (pending/active) if forms are required, otherwise timeline status
	const status = awardForms.length > 0 ? awardStatus as "pending" | "active" | "completed" : timelineStatus;
	const isExpiringSoon = timelineStatus === "active" && daysRemaining <= 30;
	const formsIncomplete = awardForms.length > 0 && !allRequiredFormsComplete;
	const solicitation = contract.solicitation as { id: string; title: string; description: string | null } | null;

	return (
		<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-start justify-between">
					<div className="space-y-1">
						<div className="flex items-center gap-2">
							<Button variant="ghost" size="icon" asChild>
								<Link to="/vendor/$buyerSlug/contracts" params={{ buyerSlug }}>
									<ArrowLeft className="h-4 w-4" />
								</Link>
							</Button>
							<Badge className={getContractStatusColor(status)}>
								{status}
							</Badge>
							{formsIncomplete && (
								<Badge variant="outline" className="text-amber-600 border-amber-300">
									Forms Required
								</Badge>
							)}
							{isExpiringSoon && (
								<Badge variant="destructive">Expiring Soon</Badge>
							)}
						</div>
						<h1 className="text-2xl font-bold tracking-tight">
							{solicitation?.title ?? "Contract Details"}
						</h1>
						<div className="flex items-center gap-4 text-sm text-muted-foreground">
							<span className="flex items-center gap-1">
								<Building2 className="h-4 w-4" />
								{contract.organization?.name ?? "Unknown Organization"}
							</span>
							<span className="flex items-center gap-1">
								<Calendar className="h-4 w-4" />
								Awarded {new Date(contract.awardedAt).toLocaleDateString()}
							</span>
						</div>
					</div>
					{solicitation && (
						<Button variant="outline" asChild>
							<Link
								to="/public/$slug/solicitations/$solicitationId"
								params={{ slug: buyerSlug, solicitationId: solicitation.id }}
							>
								<ExternalLink className="mr-2 h-4 w-4" />
								View Solicitation
							</Link>
						</Button>
					)}
				</div>

				{/* Contract Timeline */}
				{startDate && endDate && (
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center justify-between mb-4">
								<div>
									<p className="font-medium">Contract Timeline</p>
									<p className="text-sm text-muted-foreground">
										{startDate.toLocaleDateString()} -{" "}
										{endDate.toLocaleDateString()}
									</p>
								</div>
								<div className="text-right">
									<p className="text-2xl font-bold">{daysRemaining}</p>
									<p className="text-sm text-muted-foreground">days remaining</p>
								</div>
							</div>
							<Progress value={percentComplete} className="h-3" />
							<div className="flex justify-between mt-2 text-sm text-muted-foreground">
								<span>Start</span>
								<span>{percentComplete}% complete</span>
								<span>End</span>
							</div>
						</CardContent>
					</Card>
				)}

				<div className="grid gap-6 lg:grid-cols-3">
					{/* Main Content */}
					<div className="lg:col-span-2 space-y-6">
						{/* Contract Summary */}
						<Card>
							<CardHeader>
								<CardTitle>Contract Summary</CardTitle>
							</CardHeader>
							<CardContent className="space-y-4">
								<p className="text-muted-foreground">
									{solicitation?.description ?? "No description available."}
								</p>

								{startDate && endDate && (
									<div className="p-4 border rounded-lg">
										<div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
											<Clock className="h-4 w-4" />
											Duration
										</div>
										<p className="text-xl font-bold">
											{(() => {
												const months = (endDate.getFullYear() - startDate.getFullYear()) * 12
													+ (endDate.getMonth() - startDate.getMonth());
												return months;
											})()}{" "}
											months
										</p>
									</div>
								)}

								{contract.notes && (
									<div className="p-4 bg-muted/50 rounded-lg">
										<p className="text-sm font-medium mb-1">Award Notes</p>
										<p className="text-sm text-muted-foreground">{contract.notes}</p>
									</div>
								)}
							</CardContent>
						</Card>

						{/* Contract Documents */}
						{contract.contractDocuments.length > 0 && (
							<Card>
								<CardHeader>
									<CardTitle className="flex items-center gap-2">
										<FileText className="h-5 w-5" />
										Contract Documents
									</CardTitle>
									<CardDescription>
										Download contract and supporting documents
									</CardDescription>
								</CardHeader>
								<CardContent className="space-y-3">
									{contract.contractDocuments.map((doc) => (
										<div
											key={doc.id}
											className="flex items-center justify-between p-3 border rounded-lg"
										>
											<div className="flex items-center gap-3">
												<FileText
													className={`h-5 w-5 ${
														doc.document_type === "award"
															? "text-green-600"
															: doc.document_type === "contract"
																? "text-blue-600"
																: "text-muted-foreground"
													}`}
												/>
												<div>
													<span className="font-medium">{doc.title}</span>
													{doc.description && (
														<p className="text-sm text-muted-foreground">
															{doc.description}
														</p>
													)}
												</div>
											</div>
											<Tooltip>
												<TooltipTrigger asChild>
													<Button
														variant="ghost"
														size="sm"
														onClick={() =>
															toast.success(`Downloading ${doc.title}...`)
														}
													>
														<Download className="h-4 w-4" />
													</Button>
												</TooltipTrigger>
												<TooltipContent>Download: {doc.title}</TooltipContent>
											</Tooltip>
										</div>
									))}
								</CardContent>
							</Card>
						)}

						{/* Award Forms */}
						{awardForms.length > 0 && (
							<Card>
								<CardHeader>
									<CardTitle className="flex items-center gap-2">
										<FileText className="h-5 w-5" />
										Required Forms
									</CardTitle>
									<CardDescription>
										Complete these forms to activate your contract
									</CardDescription>
								</CardHeader>
								<CardContent className="space-y-3">
									{!allRequiredFormsComplete && (
										<div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
											<p className="text-sm text-amber-800">
												Please complete all required forms to finalize your contract.
												{requiredForms.length > 0 && (
													<span className="block mt-1 text-amber-600">
														{completedRequiredForms.length} of {requiredForms.length} required forms completed
													</span>
												)}
											</p>
										</div>
									)}
									{awardForms.map((form) => {
										const effectiveStatus = getEffectiveFormStatus(form.id);
										const isComplete = effectiveStatus === "submitted";
										const isDraft = effectiveStatus === "draft";
										return (
											<div
												key={form.id}
												className={`flex items-center justify-between p-3 border rounded-lg ${
													isComplete
														? "bg-green-50 border-green-200"
														: isDraft
															? "bg-amber-50 border-amber-200"
															: ""
												}`}
											>
												<div className="flex items-center gap-3">
													{isComplete ? (
														<CheckCircle2 className="h-5 w-5 text-green-600" />
													) : isDraft ? (
														<Clock className="h-5 w-5 text-amber-600" />
													) : (
														<FileText className="h-5 w-5 text-muted-foreground" />
													)}
													<div>
														<div className="flex items-center gap-2">
															<span className="font-medium">{form.title}</span>
															{form.is_required && (
																<Badge variant="default" className="text-xs">
																	Required
																</Badge>
															)}
														</div>
														{form.description && (
															<p className="text-sm text-muted-foreground">
																{form.description}
															</p>
														)}
													</div>
												</div>
												<div className="flex items-center gap-2">
													<Badge
														variant={isComplete ? "default" : "outline"}
														className={
															isComplete
																? "bg-green-600"
																: isDraft
																	? "text-amber-600 border-amber-300"
																	: ""
														}
													>
														{isComplete ? "Completed" : isDraft ? "Draft" : "Not Started"}
													</Badge>
													<Button
														size="sm"
														variant={isComplete ? "outline" : "default"}
														onClick={() => handleOpenFormFiller(form)}
													>
														{isComplete ? "View" : isDraft ? "Continue" : "Fill Out"}
													</Button>
												</div>
											</div>
										);
									})}
								</CardContent>
							</Card>
						)}

						{/* Contract Periods */}
						{contract.contractDates.length > 0 && (
							<Card>
								<CardHeader>
									<CardTitle>Contract Periods</CardTitle>
									<CardDescription>
										Contract period dates and timeline
									</CardDescription>
								</CardHeader>
								<CardContent>
									<div className="space-y-4">
										{contract.contractDates.map((period, index) => {
											const periodStart = new Date(period.start_date);
											const periodEnd = new Date(period.end_date);
											const periodCompleted = now > periodEnd;
											const periodActive = now >= periodStart && now <= periodEnd;

											return (
												<div key={period.id} className="flex items-start gap-4">
													<div className="flex flex-col items-center">
														<div
															className={`w-8 h-8 rounded-full flex items-center justify-center ${
																periodCompleted
																	? "bg-blue-100"
																	: periodActive
																		? "bg-green-100"
																		: "bg-gray-100"
															}`}
														>
															{periodCompleted ? (
																<CheckCircle2 className="h-5 w-5 text-blue-600" />
															) : (
																<Clock className={`h-5 w-5 ${periodActive ? "text-green-600" : "text-gray-400"}`} />
															)}
														</div>
														{index < contract.contractDates.length - 1 && (
															<div
																className={`w-0.5 h-8 mt-1 ${
																	periodCompleted
																		? "bg-blue-200"
																		: "bg-gray-200"
																}`}
															/>
														)}
													</div>
													<div className="flex-1 pb-4">
														<div className="flex items-center justify-between">
															<div>
																<p
																	className={`font-medium ${periodCompleted ? "" : "text-muted-foreground"}`}
																>
																	{period.period_label ?? `Period ${period.period_number}`}
																</p>
																{period.is_extension && (
																	<Badge variant="outline" className="mt-1">
																		Extension
																	</Badge>
																)}
															</div>
															<span className="text-sm text-muted-foreground">
																{periodStart.toLocaleDateString()} - {periodEnd.toLocaleDateString()}
															</span>
														</div>
													</div>
												</div>
											);
										})}
									</div>
								</CardContent>
							</Card>
						)}
					</div>

					{/* Sidebar */}
					<div className="space-y-6">
						{/* Organization Info */}
						<Card>
							<CardHeader>
								<CardTitle>Organization</CardTitle>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="flex items-center gap-3">
									<div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
										<Building2 className="h-5 w-5 text-muted-foreground" />
									</div>
									<div>
										<p className="font-medium">
											{contract.organization?.name ?? "Unknown"}
										</p>
									</div>
								</div>
							</CardContent>
						</Card>

						{/* Key Dates */}
						<Card>
							<CardHeader>
								<CardTitle>Key Dates</CardTitle>
							</CardHeader>
							<CardContent className="space-y-3">
								<div>
									<p className="text-sm text-muted-foreground">Award Date</p>
									<p className="font-medium">
										{new Date(contract.awardedAt).toLocaleDateString()}
									</p>
								</div>
								{startDate && (
									<>
										<Separator />
										<div>
											<p className="text-sm text-muted-foreground">
												Contract Start
											</p>
											<p className="font-medium">
												{startDate.toLocaleDateString()}
											</p>
										</div>
									</>
								)}
								{endDate && (
									<div>
										<p className="text-sm text-muted-foreground">Contract End</p>
										<p className="font-medium">
											{endDate.toLocaleDateString()}
										</p>
									</div>
								)}
							</CardContent>
						</Card>

						{/* Quick Stats */}
						<Card>
							<CardHeader>
								<CardTitle>Contract Stats</CardTitle>
							</CardHeader>
							<CardContent className="space-y-3">
								<div className="flex justify-between">
									<span className="text-muted-foreground">Progress</span>
									<span className="font-medium">
										{percentComplete}%
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Days Remaining</span>
									<span className="font-medium">{daysRemaining}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-muted-foreground">Documents</span>
									<span className="font-medium">{contract.contractDocuments.length}</span>
								</div>
								{awardForms.length > 0 && (
									<div className="flex justify-between">
										<span className="text-muted-foreground">Forms</span>
										<span className={`font-medium ${allRequiredFormsComplete ? "text-green-600" : "text-amber-600"}`}>
											{completedRequiredForms.length}/{requiredForms.length}
										</span>
									</div>
								)}
								<div className="flex justify-between">
									<span className="text-muted-foreground">Periods</span>
									<span className="font-medium">{contract.contractDates.length}</span>
								</div>
							</CardContent>
						</Card>
					</div>
				</div>
			</div>

			{/* Form Filler Modal */}
			<PdfFormFillerV2
				open={!!formToFill}
				onClose={() => setFormToFill(null)}
				onSave={handleFormComplete}
				formName={formToFill?.title ?? ""}
				formDescription={formToFill?.description ?? undefined}
				formTemplateId={formToFill?.form_templates?.id ?? ""}
				existingResponses={
					formToFill
						? (getSubmissionForForm(formToFill.id)?.field_responses ?? {})
						: {}
				}
				requireSignature={true}
				signerName={`${vendorProfile.first_name ?? ""} ${vendorProfile.last_name ?? ""}`.trim()}
				signerEmail={signerEmail}
			/>
		</VendorLayout>
	);
}
